import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

function SetupHike() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    totalDistance: '',
    preHikeBudget: '',
    onTrailBudget: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

// Validation
if (!formData.name || !formData.totalDistance || !formData.onTrailBudget) {
  setError('Name, distance, and on-trail budget are required');
  setLoading(false);
  return;
}

if (parseFloat(formData.totalDistance) <= 0) {
  setError('Total distance must be greater than 0');
  setLoading(false);
  return;
}

if (parseFloat(formData.onTrailBudget) <= 0) {
  setError('On-trail budget must be greater than 0');
  setLoading(false);
  return;
}

if (formData.preHikeBudget && parseFloat(formData.preHikeBudget) < 0) {
  setError('Pre-hike budget cannot be negative');
  setLoading(false);
  return;
}

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hikes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: formData.name,
          totalDistance: parseFloat(formData.totalDistance),
          preHikeBudget: parseFloat(formData.preHikeBudget) || 0,
          onTrailBudget: parseFloat(formData.onTrailBudget)
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create hike');
      }

      // Success - redirect to update hike page
      navigate('/hike/update');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Please log in to set up a hike.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <h1 className="text-3xl font-bold mb-6">Setup New Hike</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Hike Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="e.g., Appalachian Trail 2025"
            required
          />
          <p className="text-gray-600 text-xs mt-1">Give your hike a memorable name</p>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="totalDistance">
            Total Distance (km) *
          </label>
          <input
            type="number"
            id="totalDistance"
            name="totalDistance"
            value={formData.totalDistance}
            onChange={handleChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="e.g., 3500"
            step="0.1"
            min="0"
            required
          />
          <p className="text-gray-600 text-xs mt-1">Total distance you plan to hike</p>
        </div>

        {/* Pre-Hike Budget */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="preHikeBudget">
            Pre-Hike Budget ($) - Optional
          </label>
          <input
            type="number"
            id="preHikeBudget"
            name="preHikeBudget"
            value={formData.preHikeBudget}
            onChange={handleChange}
            step="0.01"
            min="0"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="500.00"
          />
          <p className="text-xs text-gray-600 mt-1">
            Budget for gear, flights, and other pre-hike expenses
          </p>
        </div>

        {/* On-Trail Budget */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="onTrailBudget">
            On-Trail Budget ($)
          </label>
          <input
            type="number"
            id="onTrailBudget"
            name="onTrailBudget"
            value={formData.onTrailBudget}
            onChange={handleChange}
            step="0.01"
            min="0"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="1000.00"
            required
          />
          <p className="text-xs text-gray-600 mt-1">
            Budget for daily trail expenses (food, accommodation, etc.)
          </p>
        </div>

        <div className="flex items-center justify-between">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:bg-gray-400"
          >
            {loading ? 'Creating...' : 'Create Hike'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/hike/update')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancel
          </button>
        </div>
      </form>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
        <p className="text-sm text-gray-700">
          <strong>Tip:</strong> You can create multiple hikes and track them separately. 
          Each hike will have its own entries and statistics.
        </p>
      </div>
    </div>
  );
}

export default SetupHike;
